import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import {
  etlIntegrationsApi,
  GMR_TRANSPORT_EVENT_TYPES,
} from '@rsos/reduxDataStore/src/features/resourceRequests/etlIntegrationsApi';

const GMR_EVENT_TYPES = new Set<string>([
  GMR_TRANSPORT_EVENT_TYPES.REQUESTED,
  GMR_TRANSPORT_EVENT_TYPES.STATUS_UPDATED,
]);

/**
 * Subscribes to StreamWS and invalidates the getTransports RTK Query cache when
 * GMR transport websocket events arrive. Matches other streamWS_hooks patterns.
 */
const useHandleGmrTransportMessages = (): void => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch =
    useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  const enabled = useHasAccessToAirAmbulance();

  const invalidateTransports = useCallback(() => {
    dispatch(etlIntegrationsApi.util.invalidateTags(['Transports']));
  }, [dispatch]);

  const handleGmrTransportMessage = useCallback(
    (message: Record<string, unknown>) => {
      if (GMR_EVENT_TYPES.has(message?.type as string)) {
        invalidateTransports();
      }
    },
    [invalidateTransports],
  );

  useEffect(() => {
    if (!StreamWS || !enabled) {
      return;
    }

    StreamWS.addOnMessageCallback({
      handleGmrTransportMessage,
    });
    StreamWS.addOnReconnectCallback({
      handleGmrTransportReconnect: invalidateTransports,
    });

    return () => {
      StreamWS.removeOnMessageCallback('handleGmrTransportMessage');
      StreamWS.removeOnReconnectCallback('handleGmrTransportReconnect');
    };
  }, [StreamWS, handleGmrTransportMessage, invalidateTransports, enabled]);
};

export default useHandleGmrTransportMessages;
